import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react'

export type ScheduleListPaginationPageSize = number | 'All'

export interface UseScheduleListPaginationOptions {
  initialPageSize?: ScheduleListPaginationPageSize
  allLimit?: number
}

export interface UseScheduleListPaginationResult {
  pageSize: ScheduleListPaginationPageSize
  setPageSize: (pageSize: ScheduleListPaginationPageSize) => void
  currentPage: number
  setCurrentPage: Dispatch<SetStateAction<number>>
  effectiveLimit: number
  effectiveOffset: number
}

export interface UseScheduleListPageClampOptions {
  pageSize: ScheduleListPaginationPageSize
  currentPage: number
  setCurrentPage: Dispatch<SetStateAction<number>>
  totalCount: number
  isLoading?: boolean
  hasError?: boolean
}

export const useScheduleListPagination = ({
  initialPageSize = 25,
  allLimit = 10000,
}: UseScheduleListPaginationOptions = {}): UseScheduleListPaginationResult => {
  const [pageSize, setPageSizeState] =
    useState<ScheduleListPaginationPageSize>(initialPageSize)
  const [currentPage, setCurrentPage] = useState(1)

  const effectiveLimit = pageSize === 'All' ? allLimit : pageSize
  const effectiveOffset = pageSize === 'All' ? 0 : (currentPage - 1) * pageSize

  const setPageSize = useCallback(
    (nextPageSize: ScheduleListPaginationPageSize) => {
      setPageSizeState(nextPageSize)
      setCurrentPage(1)
    },
    [],
  )

  return {
    pageSize,
    setPageSize,
    currentPage,
    setCurrentPage,
    effectiveLimit,
    effectiveOffset,
  }
}

/**
 * Keep current page valid when total count or page size changes.
 * This is extracted to avoid duplicating clamp logic in app pages.
 */
export const useScheduleListPageClamp = ({
  pageSize,
  currentPage,
  setCurrentPage,
  totalCount,
  isLoading = false,
  hasError = false,
}: UseScheduleListPageClampOptions): void => {
  useEffect(() => {
    if (pageSize === 'All') {
      if (currentPage !== 1) {
        setCurrentPage(1)
      }
      return
    }

    if (isLoading || hasError || totalCount <= 0) {
      return
    }

    const totalPages = Math.max(1, Math.ceil(totalCount / pageSize))
    if (currentPage > totalPages) {
      setCurrentPage(totalPages)
    }
  }, [currentPage, hasError, isLoading, pageSize, setCurrentPage, totalCount])
}
